import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Clock, MapPin, ChevronRight, Plane, Globe } from 'lucide-react';
import RouteMap from '@/components/RouteMap';
import Layout from '@/components/Layout';
import { DESTINATIONS, FLIGHT_SCHEDULES, ROUTE_PATHS } from '@/lib/index';
import { springPresets } from '@/lib/motion';

const ROUTE_COMPARE = [
  { dest: '达累斯萨拉姆 (DAR)', tc: '12:45', et: '16:00', qr: '17:55', kq: '16:25', ek: '18:25', tcBest: true },
  { dest: '桑给巴尔 (ZNZ)',     tc: '11:15', et: '15:40', qr: '17:45', kq: '16:05', ek: '19:25', tcBest: true },
  { dest: '约翰内斯堡 (JNB)',   tc: '18:05', et: '18:25', qr: '19:55', kq: '19:15', ek: '22:20', tcBest: false },
  { dest: '内罗毕 (NBO)',       tc: '—',     et: '18:55', qr: '17:50', kq: '13:25', ek: '19:25', tcBest: false },
];

// 中国航线（1条）
const CHINA_CITIES = ['广州 (CAN)'];
// 中东/南亚航线（2城市）
const SOUTH_ASIA_CITIES = ['孟买 (BOM)', '迪拜 (DXB)'];
// 区域/非洲国际航线（15城市）
const INTL_CITIES = [
  '内罗毕 (NBO)', '约翰内斯堡 (JNB)', '开普敦 (CPT)', '拉各斯 (LOS)',
  '卢本巴希 (FBM)', '金沙萨 (FIH)', '卢萨卡 (LUN)', '恩多拉 (NLA)',
  '哈拉雷 (HRE)', '布琼布拉 (BJM)', '恩德培 (EBB)', '科摩罗 (HAH)',
  '维多利亚瀑布 (VFA)', '🆕 塞舌尔 (SEZ)',
];
// 坦桑尼亚国内航线（13城市）
const DOM_CITIES = [
  '桑给巴尔 (ZNZ)', '乞力马扎罗 (JRO)', '阿鲁沙 (ARK)', '姆万扎 (MWZ)',
  '布科巴 (BKZ)', '多多马 (DOD)', '基戈马 (TKQ)', '姆贝亚 (MBI)',
  '姆特瓦拉 (MYW)', '松盖亚 (SGX)', '伊灵加 (IRI)', '林迪 (LDI)', '塔波拉 (TBO)',
];
// 计划新开航点
const PLANNED_CITIES = ['马普托 (MPM)', '布拉柴维尔 (BZV)', '戈马 (GOM)', '伦敦 (LON)'];

type Tab = 'guangzhou' | 'network' | 'destinations';

export default function Routes() {
  const [tab, setTab] = useState<Tab>('guangzhou');
  const [selDest, setSelDest] = useState(DESTINATIONS[0]);

  return (
    <Layout>
      {/* Hero full‑bleed */}
      <section className="relative h-80 overflow-hidden">
        <img src="/images/25x21.5cm (Regional&Int Routes)_gYUbNj.jpg"
          alt="航线网络" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/55 to-secondary/85" />
        <div className="absolute inset-0 flex items-end pb-10 pt-20">
          <div className="max-w-7xl mx-auto px-6 w-full">
            <h1 className="text-5xl font-bold text-white mb-1">航线与目的地</h1>
            <p className="text-white/65">连接广州与非洲大陆 · 覆盖30+目的地城市</p>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <div className="bg-card border-b border-border sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-0 overflow-x-auto">
            {([
              { id: 'guangzhou', label: '广州出发航线' },
              { id: 'network', label: '全球航线网络' },
              { id: 'destinations', label: '目的地详情' },
            ] as const).map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`px-6 py-4 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                  tab === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">

        {/* ── Guangzhou tab ── */}
        {tab === 'guangzhou' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h2 className="text-2xl font-bold mb-1">广州出发航班时刻</h2>
            <p className="text-muted-foreground text-sm mb-8">T2航站楼 · 波音787-8 · 每周一三五出发</p>

            <div className="space-y-4 mb-10">
              {FLIGHT_SCHEDULES.map((f, i) => (
                <div key={i} className="group bg-card border border-border rounded-2xl overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] items-center">
                    {/* Time block */}
                    <div className="flex items-center gap-4 p-6">
                      <div className="text-center min-w-[70px]">
                        <div className="text-4xl font-bold font-mono">{f.departure}</div>
                        <div className="font-mono text-xs text-muted-foreground mt-0.5">{f.fromCode}</div>
                        <div className="text-xs text-muted-foreground">{f.from}</div>
                      </div>
                      <div className="flex flex-col items-center gap-1 flex-1 px-4">
                        <Plane size={16} className="text-primary" />
                        <div className="w-full h-px bg-border" />
                        <span className="text-xs text-muted-foreground">{f.aircraft}</span>
                      </div>
                      <div className="text-center min-w-[70px]">
                        <div className="text-4xl font-bold font-mono">{f.arrival}</div>
                        <div className="font-mono text-xs text-muted-foreground mt-0.5">{f.toCode}</div>
                        <div className="text-xs text-muted-foreground">{f.to}</div>
                      </div>
                      <div className="ml-6 hidden sm:flex flex-col gap-1">
                        <span className="font-mono text-xs font-bold text-primary">{f.flightNo}</span>
                        <div className="flex flex-wrap gap-1">
                          {f.days.map(d => (
                            <span key={d} className="text-[11px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-full border border-primary/20">{d}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                    {/* CTA */}
                    <div className="px-6 pb-5 md:pb-0 md:pr-6">
                      <Link to={ROUTE_PATHS.BOOKING}
                        className="flex items-center gap-2 px-5 py-2.5 bg-secondary text-white rounded-xl text-sm font-semibold hover:bg-secondary/90 transition whitespace-nowrap">
                        订票 <ArrowRight size={13} />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Transfer tips row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
              {[
                { icon: '🏠', title: '值机信息', desc: 'T2航站楼提前3小时到达，凭护照和订单办理' },
                { icon: '🔄', title: '中转政策', desc: '24小时内衔接航班行李直挂；中转DAR需清关重托' },
                { icon: '📋', title: '签证信息', desc: '坦桑尼亚落地签约USD 50；桑给巴尔免签入境' },
              ].map(item => (
                <div key={item.title} className="bg-muted rounded-2xl p-5">
                  <span className="text-2xl">{item.icon}</span>
                  <h3 className="font-semibold mt-2 mb-1">{item.title}</h3>
                  <p className="text-muted-foreground text-sm">{item.desc}</p>
                </div>
              ))}
            </div>

            {/* Comparison table */}
            <h2 className="text-lg font-bold mb-3">广州出发飞行时长对比</h2>
            <div className="overflow-x-auto rounded-2xl border border-border mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-secondary text-white">
                    <th className="px-4 py-3 text-left">目的地</th>
                    <th className="px-4 py-3 text-center text-accent font-bold">TC 坦航</th>
                    <th className="px-4 py-3 text-center">ET 埃航</th>
                    <th className="px-4 py-3 text-center">QR 卡航</th>
                    <th className="px-4 py-3 text-center">KQ 肯航</th>
                    <th className="px-4 py-3 text-center">EK 阿联酋</th>
                  </tr>
                </thead>
                <tbody>
                  {ROUTE_COMPARE.map((r, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-background' : 'bg-muted/40'}>
                      <td className="px-4 py-3 font-medium">{r.dest}</td>
                      <td className={`px-4 py-3 text-center font-mono font-bold ${r.tcBest ? 'text-primary' : ''}`}>
                        {r.tcBest && '🏆 '}{r.tc}
                      </td>
                      <td className="px-4 py-3 text-center font-mono text-muted-foreground">{r.et}</td>
                      <td className="px-4 py-3 text-center font-mono text-muted-foreground">{r.qr}</td>
                      <td className="px-4 py-3 text-center font-mono text-muted-foreground">{r.kq}</td>
                      <td className="px-4 py-3 text-center font-mono text-muted-foreground">{r.ek}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground">* 飞行时长为参考值，以实际航班为准。TC直飞不含经停等待时间。</p>
          </motion.div>
        )}

        {/* ── Network tab ── */}
        {tab === 'network' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
              <div>
                <h2 className="text-2xl font-bold mb-1">全球航线网络</h2>
                <p className="text-muted-foreground text-sm mb-6">以达累斯萨拉姆（DAR）为枢纽，辐射亚洲、非洲与中国</p>

                {/* 枢纽 */}
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-3 h-3 rounded-full bg-yellow-400 shrink-0" />
                    <h3 className="font-bold text-sm">枢纽（1城市）</h3>
                  </div>
                  <div className="flex flex-wrap gap-2 pl-5">
                    <span className="px-3 py-1.5 rounded-full text-xs bg-yellow-50 border border-yellow-300 text-yellow-800 font-semibold">达累斯萨拉姆 (DAR)</span>
                  </div>
                </div>

                {/* 中国航线 */}
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-3 h-3 rounded-full bg-orange-400 shrink-0" />
                    <h3 className="font-bold text-sm">中国航线（1城市）</h3>
                  </div>
                  <div className="flex flex-wrap gap-2 pl-5">
                    {CHINA_CITIES.map(c => (
                      <span key={c} className="px-3 py-1.5 rounded-full text-xs bg-orange-50 border border-orange-300 text-orange-800 font-semibold">{c}</span>
                    ))}
                  </div>
                </div>

                {/* 南亚航线 */}
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-3 h-3 rounded-full bg-purple-400 shrink-0" />
                    <h3 className="font-bold text-sm">中东/南亚航线（2城市）</h3>
                  </div>
                  <div className="flex flex-wrap gap-2 pl-5">
                    {SOUTH_ASIA_CITIES.map(c => (
                      <span key={c} className="px-3 py-1.5 rounded-full text-xs bg-purple-50 border border-purple-300 text-purple-800">{c}</span>
                    ))}
                  </div>
                </div>

                {/* 区域/非洲国际航线 */}
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-3 h-3 rounded-full bg-blue-400 shrink-0" />
                    <h3 className="font-bold text-sm">区域航线（15城市）</h3>
                  </div>
                  <div className="flex flex-wrap gap-2 pl-5">
                    {INTL_CITIES.map(c => (
                      <span key={c} className={`px-3 py-1.5 rounded-full text-xs border ${
                        c.includes('🆕') ? 'bg-accent/10 border-accent text-amber-700 font-semibold' : 'bg-blue-50 border-blue-200 text-blue-700'
                      }`}>{c}</span>
                    ))}
                  </div>
                </div>

                {/* 国内航线 */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-3 h-3 rounded-full bg-green-400 shrink-0" />
                    <h3 className="font-bold text-sm">坦桑尼亚国内航线（13城市）</h3>
                  </div>
                  <div className="flex flex-wrap gap-2 pl-5">
                    {DOM_CITIES.map(c => (
                      <span key={c} className="px-3 py-1.5 rounded-full text-xs bg-green-50 border border-green-200 text-green-700">{c}</span>
                    ))}
                  </div>
                </div>

                {/* 计划新开 */}
                <div className="mb-6 p-4 bg-muted rounded-2xl border border-dashed border-border">
                  <h3 className="font-bold text-sm mb-2 text-muted-foreground">🗓 计划新开航点</h3>
                  <div className="flex flex-wrap gap-2">
                    {PLANNED_CITIES.map(c => (
                      <span key={c} className="px-3 py-1.5 rounded-full text-xs bg-background border border-dashed border-muted-foreground/40 text-muted-foreground">{c}</span>
                    ))}
                  </div>
                </div>

                <div className="bg-primary/5 border border-primary/20 rounded-2xl p-5">
                  <h3 className="font-semibold text-primary text-sm mb-3">中转优势</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>✅ 广州→DAR 仅12小时45分，省3–7小时</li>
                    <li>✅ 24小时内衔接可直挂行李至第三国</li>
                    <li>✅ 经DAR中转24h内，中国护照免过境签</li>
                    <li>✅ 最短衔接≥2小时，可访问13+国内目的地</li>
                  </ul>
                </div>
              </div>

              {/* 右侧：交互地图 */}
              <div className="sticky top-24 space-y-4">
                <RouteMap />
              </div>
            </div>
          </motion.div>
        )}

        {/* ── Destinations tab ── */}
        {tab === 'destinations' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Sidebar */}
              <div className="space-y-2">
                <h2 className="font-bold text-lg mb-4">选择目的地</h2>
                {DESTINATIONS.map(d => (
                  <button key={d.id} onClick={() => setSelDest(d)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all ${
                      selDest.id === d.id ? 'bg-secondary text-white shadow-md' : 'bg-muted hover:bg-muted/80'
                    }`}>
                    <div className="w-10 h-10 rounded-lg overflow-hidden shrink-0">
                      <img src={d.image} alt={d.name} className="w-full h-full object-cover" />
                    </div>
                    <span className="font-medium text-sm">{d.name}</span>
                    {d.id === 'seychelles' && (
                      <span className="ml-auto text-xs bg-accent/80 text-accent-foreground px-1.5 py-0.5 rounded-full">新</span>
                    )}
                    <ChevronRight size={13} className="ml-auto opacity-40" />
                  </button>
                ))}
              </div>

              {/* Detail */}
              <div className="lg:col-span-2">
                <motion.div key={selDest.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={springPresets.gentle}>
                  <div className="relative h-72 rounded-2xl overflow-hidden mb-6">
                    <img src={selDest.image} alt={selDest.name} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-secondary/60 to-transparent" />
                    <div className="absolute bottom-5 left-5">
                      <span className="font-mono text-xs bg-accent/80 text-accent-foreground px-2 py-0.5 rounded-full">{selDest.code}</span>
                      <h2 className="text-3xl font-bold text-white mt-1">{selDest.name}</h2>
                      <p className="text-white/75 text-sm">{selDest.nameEn} · {selDest.tagline}</p>
                    </div>
                  </div>

                  <p className="text-muted-foreground mb-5 leading-relaxed">{selDest.description}</p>

                  <div className="grid grid-cols-2 gap-3 mb-5">
                    <div className="bg-muted rounded-xl p-4">
                      <div className="flex items-center gap-2 text-primary mb-1.5 text-xs font-semibold">
                        <Clock size={13} /> 最佳季节
                      </div>
                      <p className="text-sm">{selDest.bestSeason}</p>
                    </div>
                    <div className="bg-muted rounded-xl p-4">
                      <div className="flex items-center gap-2 text-primary mb-1.5 text-xs font-semibold">
                        <MapPin size={13} /> 气候
                      </div>
                      <p className="text-sm">{selDest.climate}</p>
                    </div>
                  </div>

                  <h3 className="font-semibold mb-3 text-sm">旅游亮点</h3>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {selDest.highlights.map(h => (
                      <span key={h} className="px-3 py-1.5 bg-primary/10 text-primary text-xs rounded-full border border-primary/20">✦ {h}</span>
                    ))}
                  </div>

                  <Link to={ROUTE_PATHS.BOOKING}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-secondary text-white rounded-xl font-semibold text-sm hover:bg-secondary/90 transition">
                    <Plane size={15} />
                    预订飞往{selDest.name}的机票
                  </Link>
                </motion.div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </Layout>
  );
}
